import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { apiFetch, InsufficientBalanceError } from "../lib/api";
import { putSegmentSpeakerOverride } from "../lib/api/speaker-override";
import { TranscriptDisplay } from "../components/transcription/transcript-display";
import { MetadataPanel } from "../components/transcription/metadata-panel";
import { audioManager } from "../services/audioManager";
import jsPDF from "jspdf";
import { SpeakerAvatar, AVATAR_COLORS } from "@/components/SpeakerAvatar";
import { Thumbnail } from "@/components/Thumbnail";
import InsufficientBalanceModal from "@/components/InsufficientBalanceModal";

// Word-level timing for karaoke highlighting
interface WordInfo {
  text: string;
  start_ms: number;
  end_ms: number;
  confidence?: number;
}

// Locked transcript_json format: always an array, real names, start_ms/end_ms present
interface Segment {
  speaker: string;
  text: string;
  start_ms: number;
  end_ms: number;
  words?: WordInfo[]; // Word-level timing (new transcripts only)
  segment_id?: string;  // DB segments.id, attached by _apply_user_segment_overrides
  user_override?: true; // Set by backend render-time join when user has a personal override
}

// Locked speakers_json format: separate speaker metadata
interface SpeakerMeta {
  id: string;
  display_name: string;
  name?: string;
  role?: string;
  bio?: string;
  confidence?: number;
  image_url?: string;
}

interface TranscriptItem {
  id: string;
  title: string;
  channel_name?: string;
  url?: string;
  youtube_url?: string;  // For RSS items, YouTube equivalent if found
  feed_url?: string;     // RSS feed URL for linking to browse page
  created_at: string;
  published_date?: string;
  source?: string;
  source_type?: string;  // e.g. 'upload' for private uploads (used to default to Edit mode)
  video_id?: string;
  summary?: string;
  duration_ms?: number;
  was_truncated?: boolean;
  transcript_json?: string;
  speakers_json?: string;
  speakers?: SpeakerMeta[];  // Enriched speakers array from API (with bios/images)
}

interface TranscriptData {
  item: TranscriptItem;
  segments: Segment[];
  durationMs: number;
}

interface Recommendation {
  video_id: string;
  title: string;
  channel_name?: string;
  summary?: string;
  thumbnail_url?: string;
  url: string;
}

function msToTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

function msToDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

type ExportFormat = "txt" | "srt" | "vtt" | "json";

function buildExport(
  format: ExportFormat,
  segments: Segment[],
  title: string,
  durationMs: number,
) {
  const lines = segments.map((seg) => ({
    speaker: seg.speaker,
    text: seg.text,
    timestamp: msToTimestamp(seg.start_ms || 0),
  }));

  let content = "";
  const filename = `${title}.${format}`;

  switch (format) {
    case "txt":
      content = lines.map((l) => `${l.speaker}: ${l.text}`).join("\n\n");
      break;

    case "srt":
      content = lines
        .map((l, i) => {
          const end = lines[i + 1]?.timestamp || msToTimestamp(durationMs);
          return `${i + 1}\n${l.timestamp},000 --> ${end},000\n${l.speaker}: ${l.text}\n`;
        })
        .join("\n");
      break;

    case "vtt":
      content =
        "WEBVTT\n\n" +
        lines
          .map((l, i) => {
            const end = lines[i + 1]?.timestamp || msToTimestamp(durationMs);
            return `${l.timestamp}.000 --> ${end}.000\n${l.speaker}: ${l.text}`;
          })
          .join("\n\n");
      break;

    case "json": {
      const wordCount = lines.reduce(
        (acc, l) => acc + l.text.split(" ").length,
        0,
      );
      const speakerCount = new Set(lines.map((l) => l.speaker)).size;
      content = JSON.stringify(
        {
          transcript: lines,
          metadata: {
            duration: msToDuration(durationMs),
            wordCount,
            speakerCount,
          },
        },
        null,
        2,
      );
      break;
    }
  }

  const blob = new Blob([content], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function TranscriptViewPage() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState<TranscriptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [unlockingRemaining, setUnlockingRemaining] = useState(false);
  const [unlockMessage, setUnlockMessage] = useState("");
  const [unlockError, setUnlockError] = useState("");
  const [balanceModal, setBalanceModal] = useState<{ minutesNeeded: number; minutesRemaining: number } | null>(null);

  // Recommendations
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);

  // Audio player state (RSS only)
  const [audioAvailable, setAudioAvailable] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioCurrentTimeMs, setAudioCurrentTimeMs] = useState(0);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [audioError, setAudioError] = useState(false);
  const [audioIsPlaying, setAudioIsPlaying] = useState(false);
  const [audioDurationMs, setAudioDurationMs] = useState(0);
  const audioRef = useRef<HTMLAudioElement>(null);

  // Standard / Edit view mode toggle
  const [viewMode, setViewMode] = useState<'standard' | 'edit'>('standard');


  // --- Editing state ---
  const [editedSegments, setEditedSegments] = useState<Segment[]>([]);
  const [dirty, setDirty] = useState(false);

  // Inline speaker editing (in badge section only — transcript inline editing handled by TranscriptDisplay)
  const [editingBadgeName, setEditingBadgeName] = useState<string | null>(null);
  const [editingBadgeValue, setEditingBadgeValue] = useState("");

  // Save state
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "success" | "error">("idle");

  // Merge undo state — transient toast lets the user reverse a merge within 15s.
  const [lastMergeUndo, setLastMergeUndo] = useState<{
    mergedSegmentIndex: number;  // index of the resulting merged segment
    splitAtMs: number;            // original boundary timestamp
    expiresAt: number;            // Date.now() + 15000
  } | null>(null);

  // Copy toast
  const [copied, setCopied] = useState(false);
  
  // URL timestamp param for deep linking (e.g., ?t=12345)
  const initialTimestampMs = searchParams.get("t") ? parseInt(searchParams.get("t")!, 10) : null;
  const fromFolderId = searchParams.get('from_folder');
  const hasScrolledToTimestamp = useRef(false);

  // Feature flag — segment speaker override (off by default; flipped post-migration)
  const speakerOverrideEnabled = (import.meta as any).env?.VITE_FEATURE_SEGMENT_SPEAKER_OVERRIDE === 'true';

  // Add to Library state (for cached transcripts not in user's library)
  const [isInLibrary, setIsInLibrary] = useState(true);
  const [addingToLibrary, setAddingToLibrary] = useState(false);

  // --- Data fetching ---

  useEffect(() => {
    // Allow both authenticated users and guests (apiFetch sends X-Guest-Session)
    if (authLoading || !id) return;

    const processTranscriptData = (item: any, segments: Segment[]) => {
      // Resolve SPEAKER_XX to real names from speakers_json
      if (item.speakers_json) {
        try {
          const metas: SpeakerMeta[] = typeof item.speakers_json === 'string'
            ? JSON.parse(item.speakers_json)
            : item.speakers_json;
          const nameMap: Record<string, string> = {};
          for (const sp of metas) {
            if (sp.id && sp.display_name) nameMap[sp.id] = sp.display_name;
          }
          if (Object.keys(nameMap).length > 0) {
            segments = segments.map((seg) => ({
              ...seg,
              speaker: nameMap[seg.speaker] || seg.speaker,
            }));
          }
        } catch { /* ignore bad speakers_json */ }
      }

      const lastSeg = segments[segments.length - 1];
      const durationMs = item.duration_ms || lastSeg?.end_ms || 0;
      setData({ item, segments, durationMs });
      setEditedSegments(segments.map((s) => ({ ...s })));
      setDirty(false);

      // Private uploads default to Edit mode (users uploaded them specifically to edit/refine the transcript)
      if (item.source_type === 'upload') {
        setViewMode('edit');
      }
    };

    setLoading(true);
    apiFetch<any>(`/library/items/${id}`)
      .then((res) => {
        const item = res.item || res;
        const segments: Segment[] = item.transcript_json
          ? JSON.parse(item.transcript_json)
          : [];
        setIsInLibrary(true);
        processTranscriptData(item, segments);
      })
      .catch(async (err) => {
        // If 404/403, try fetching from cache (transcript exists but not in user's library)
        try {
          const cacheRes = await apiFetch<any>(`/podcasts/cache/${id}`);
          if (cacheRes && cacheRes.item) {
            const item = cacheRes.item;
            const segments: Segment[] = cacheRes.segments || [];
            setIsInLibrary(false);  // Show "Add to Library" button
            processTranscriptData(item, segments);
            return;
          }
        } catch {
          // Cache also failed - show error
        }
        setError(err instanceof Error ? err.message : "Failed to load transcript");
      })
      .finally(() => setLoading(false));
  }, [authLoading, id]);

  useEffect(() => {
    if (!data?.item.video_id) return;
    // Skip recommendations for RSS items — they use fake rss_* video IDs
    if (data.item.source === "rss" || data.item.video_id.startsWith("rss_")) return;
    apiFetch<{ recommendations: Recommendation[] }>(
      `/recommendations?video_id=${encodeURIComponent(data.item.video_id)}`,
    )
      .then((res) => setRecommendations(res.recommendations ?? []))
      .catch(() => {});
  }, [data?.item.video_id, data?.item.source]);

  // Compute initial scroll target index from URL timestamp
  const initialScrollToIndex = useMemo(() => {
    if (!data || !initialTimestampMs) return undefined;
    
    const segments = data.segments;
    if (!segments || segments.length === 0) return undefined;
    
    // Find the segment that contains this timestamp (or closest before it)
    let targetIndex = 0;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const startMs = seg.start_ms ?? 0;
      if (startMs <= initialTimestampMs) {
        targetIndex = i;
      } else {
        break;
      }
    }
    
    console.log(`[TranscriptView] Initial scroll target: segment ${targetIndex} for timestamp ${initialTimestampMs}ms`);
    return targetIndex;
  }, [data, initialTimestampMs]);

  // Handle audio seek when initial scroll completes (or on mount with timestamp)
  const handleInitialScrollComplete = useCallback(() => {
    if (!initialTimestampMs || hasScrolledToTimestamp.current) return;
    hasScrolledToTimestamp.current = true;
    
    // Seek audio if available
    if (audioRef.current) {
      console.log(`[TranscriptView] Seeking audio to ${initialTimestampMs}ms`);
      audioRef.current.currentTime = initialTimestampMs / 1000;
      audioRef.current.play().catch(() => {});
    }
    
    // Update the audio time state for highlighting
    setAudioCurrentTimeMs(initialTimestampMs);
  }, [initialTimestampMs]);

  // Check audio availability and set source (RSS sources only — YouTube blocked by API)
  const fetchAudioInfo = useCallback(async (videoId: string) => {
    console.log(`[Audio] Fetching info for video_id=${videoId}`);
    setAudioLoading(true);
    setAudioError(false);
    
    try {
      const res = await apiFetch<{ 
        available: boolean; 
        playback_allowed?: boolean; 
        playback_url?: string; 
        stream_type?: string 
      }>(`/audio/${encodeURIComponent(videoId)}/info`);
      
      console.log(`[Audio] API response:`, res);
      const isAvailable = res.available && res.playback_allowed !== false;
      
      if (!isAvailable) {
        console.log(`[Audio] Not available`);
        setAudioAvailable(false);
        setAudioSrc(null);
        return;
      }
      
      setAudioAvailable(true);
      
      if (res.stream_type === 'redirect' && res.playback_url) {
        // Use direct RSS URL - no preloading needed
        console.log(`[Audio] Using direct URL: ${res.playback_url}`);
        setAudioSrc(res.playback_url);
      } else {
        // Proxy stream — set src immediately so audio element mounts, cache in background
        console.log(`[Audio] Using proxy stream`);
        setAudioSrc(audioManager.getAudioUrl(videoId));
        // Preload in background for caching; don't block UI
        audioManager.preloadAudio(videoId).catch(() => {});
      }
    } catch (err) {
      console.error(`[Audio] Info fetch failed:`, err);
      setAudioError(true);
      setAudioAvailable(false);
      setAudioSrc(null);
    } finally {
      setAudioLoading(false);
    }
  }, []);
  
  useEffect(() => {
    if (!data?.item.video_id) return;
    
    const isRssSource = data.item.source === "rss" || data.item.video_id.startsWith("rss_");
    console.log(`[Audio] Checking audio for video_id=${data.item.video_id}, source=${data.item.source}, isRss=${isRssSource}`);
    
    if (!isRssSource) {
      console.log(`[Audio] Skipping - not RSS source`);
      setAudioAvailable(false);
      setAudioSrc(null);
      return;
    }
    
    fetchAudioInfo(data.item.video_id);
  }, [data?.item.video_id, data?.item.source, fetchAudioInfo]);

  // Subscribe to audioManager state changes - update audioSrc when cache becomes ready
  useEffect(() => {
    if (!data?.item.video_id) return;
    const videoId = data.item.video_id;
    const unsubscribe = audioManager.subscribe(videoId, {
      onStateChange: (state) => {
        if (state === 'ready' && audioManager.isCached(videoId)) {
          setAudioSrc(audioManager.getAudioUrl(videoId));
        }
      }
    });
    return unsubscribe;
  }, [data?.item.video_id]);

  // Revoke blob URLs on cleanup to prevent memory leaks
  useEffect(() => {
    return () => {
      if (audioSrc && audioSrc.startsWith('blob:')) {
        URL.revokeObjectURL(audioSrc);
      }
    };
  }, [audioSrc]);


  // --- Beforeunload warning ---

  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // --- Callbacks for TranscriptDisplay ---

  const handleEditSpeaker = useCallback((oldName: string, newName: string) => {
    setEditedSegments((prev) =>
      prev.map((seg) => (seg.speaker === oldName ? { ...seg, speaker: newName } : seg)),
    );
    setDirty(true);
  }, []);

  const handleEditSpeakerSingle = useCallback((segmentIdx: number, newName: string) => {
    setEditedSegments((prev) => {
      const next = [...prev];
      next[segmentIdx] = { ...next[segmentIdx], speaker: newName };
      return next;
    });
    setDirty(true);
  }, []);

  const handleEditText = useCallback((index: number, newText: string) => {
    setEditedSegments((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], text: newText };
      return next;
    });
    setDirty(true);
  }, []);

  const handleEditSegmentTs = useCallback((index: number, startMs: number, endMs: number) => {
    setEditedSegments((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], start_ms: startMs, end_ms: endMs };
      return next;
    });
    setDirty(true);
  }, []);

  const handleEditWordTs = useCallback((segIdx: number, wordIdx: number, startMs: number, endMs: number) => {
    setEditedSegments((prev) => {
      const next = [...prev];
      const seg = { ...next[segIdx] };
      if (seg.words) {
        const words = [...seg.words];
        words[wordIdx] = { ...words[wordIdx], start_ms: startMs, end_ms: endMs };
        seg.words = words;
      }
      next[segIdx] = seg;
      return next;
    });
    setDirty(true);
  }, []);

  /** Re-fetch segments after a split or merge, applying speaker name resolution. */
  const refreshSegmentsAfterEdit = useCallback(async () => {
    if (!id) return;
    const res = await apiFetch<any>(`/library/items/${id}`);
    const freshItem = res.item || res;
    let freshSegments: Segment[] = freshItem.transcript_json
      ? JSON.parse(freshItem.transcript_json)
      : [];
    if (freshItem.speakers_json) {
      try {
        const metas: SpeakerMeta[] = typeof freshItem.speakers_json === 'string'
          ? JSON.parse(freshItem.speakers_json)
          : freshItem.speakers_json;
        const nameMap: Record<string, string> = {};
        for (const sp of metas) {
          if (sp.id && sp.display_name) nameMap[sp.id] = sp.display_name;
        }
        if (Object.keys(nameMap).length > 0) {
          freshSegments = freshSegments.map((seg) => ({
            ...seg,
            speaker: nameMap[seg.speaker] || seg.speaker,
          }));
        }
      } catch { /* ignore bad speakers_json */ }
    }
    setData((prev) => prev ? { ...prev, segments: freshSegments } : prev);
    setEditedSegments(freshSegments.map((s) => ({ ...s })));
    setDirty(false);
  }, [id]);

  /** Call split-segment API, then re-fetch item to refresh state. Throws on API error.
   *  Third arg accepted but ignored - kept for type compat with onSplit prop signature.
   *  Speaker assignment derived server-side from segment context; FE no longer sends new_speaker.
   */
  const handleSplit = useCallback(async (segmentIdx: number, splitAtMs: number, _newSpeaker?: undefined) => {
    if (!id) return;
    await apiFetch(`/library/items/${id}/split-segment`, {
      method: 'POST',
      body: JSON.stringify({
        segment_index: segmentIdx,
        split_at_ms: splitAtMs,
      }),
    });
    await refreshSegmentsAfterEdit();
  }, [id, refreshSegmentsAfterEdit]);

  /** Merge segment segmentIdx with the segment before it.
   *  POSTs {first_segment_index: segmentIdx - 1}. Guard: segmentIdx > 0.
   *  Speaker assignment derived server-side; FE does NOT send new_speaker.
   */
  const handleMergeWithPrev = useCallback(async (segmentIdx: number) => {
    if (!id || segmentIdx <= 0) return;
    const boundaryMs = editedSegments[segmentIdx].start_ms;  // boundary = start of the segment being absorbed
    await apiFetch(`/library/items/${id}/merge-segments`, {
      method: 'POST',
      body: JSON.stringify({ first_segment_index: segmentIdx - 1 }),
    });
    await refreshSegmentsAfterEdit();
    setLastMergeUndo({
      mergedSegmentIndex: segmentIdx - 1,
      splitAtMs: boundaryMs,
      expiresAt: Date.now() + 15000,
    });
  }, [id, editedSegments, refreshSegmentsAfterEdit]);

  /** Merge segment segmentIdx with the segment after it.
   *  POSTs {first_segment_index: segmentIdx}. Guard: segmentIdx < segments.length - 1.
   *  Speaker assignment derived server-side; FE does NOT send new_speaker.
   */
  const handleMergeWithNext = useCallback(async (segmentIdx: number) => {
    if (!id || segmentIdx >= editedSegments.length - 1) return;
    const boundaryMs = editedSegments[segmentIdx + 1].start_ms;  // boundary = start of the next segment
    await apiFetch(`/library/items/${id}/merge-segments`, {
      method: 'POST',
      body: JSON.stringify({ first_segment_index: segmentIdx }),
    });
    await refreshSegmentsAfterEdit();
    setLastMergeUndo({
      mergedSegmentIndex: segmentIdx,
      splitAtMs: boundaryMs,
      expiresAt: Date.now() + 15000,
    });
  }, [id, editedSegments, refreshSegmentsAfterEdit]);

  /** Re-fetch after a per-segment speaker override to pick up updated user_override flags. */
  const handleSegmentSpeakerOverride = useCallback(async (segmentId: string, newSpeaker: string) => {
    if (!id) return;
    await putSegmentSpeakerOverride(id, segmentId, newSpeaker);
    await refreshSegmentsAfterEdit();
  }, [id, refreshSegmentsAfterEdit]);

  // Auto-expire the merge-undo toast after its 15s window elapses.
  useEffect(() => {
    if (!lastMergeUndo) return;
    const remaining = lastMergeUndo.expiresAt - Date.now();
    if (remaining <= 0) {
      setLastMergeUndo(null);
      return;
    }
    const t = setTimeout(() => setLastMergeUndo(null), remaining);
    return () => clearTimeout(t);
  }, [lastMergeUndo]);

  /** Reverse the most recent merge by splitting the merged segment at the original boundary. */
  const handleUndoMerge = useCallback(async () => {
    if (!lastMergeUndo) return;
    const { mergedSegmentIndex, splitAtMs } = lastMergeUndo;
    setLastMergeUndo(null);
    await handleSplit(mergedSegmentIndex, splitAtMs);
  }, [lastMergeUndo, handleSplit]);

  // --- Badge editing ---

  const startBadgeEdit = useCallback((name: string) => {
    setEditingBadgeName(name);
    setEditingBadgeValue(name);
  }, []);

  const commitBadgeEdit = useCallback(() => {
    if (!editingBadgeName) return;
    const oldName = editingBadgeName;
    const newName = editingBadgeValue.trim();
    if (!newName || newName === oldName) {
      setEditingBadgeName(null);
      return;
    }
    handleEditSpeaker(oldName, newName);
    setEditingBadgeName(null);
  }, [editingBadgeName, editingBadgeValue, handleEditSpeaker]);

  const cancelBadgeEdit = useCallback(() => {
    setEditingBadgeName(null);
  }, []);

  // --- Save / Discard ---

  async function handleSave() {
    if (!data || !id) return;
    setSaveStatus("saving");
    try {
      await apiFetch(`/library/items/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ transcript_json: editedSegments }),
      });
      setData((prev) =>
        prev ? { ...prev, segments: editedSegments.map((s) => ({ ...s })) } : prev,
      );
      setDirty(false);
      setSaveStatus("success");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  }

  // --- Add to Library (for cached transcripts) ---

  async function handleAddToLibrary() {
    if (!data?.item) return;
    
    const { feed_url, audio_url } = data.item as any;
    if (!feed_url || !audio_url) {
      setError("Cannot add to library: missing feed or audio URL");
      return;
    }
    
    setAddingToLibrary(true);
    try {
      const res = await apiFetch<{ item_id?: string; job_id?: string; status?: string }>("/podcasts/transcribe", {
        method: "POST",
        body: JSON.stringify({ feed_url, audio_url }),
      });
      
      // If cache hit, we get item_id immediately
      if (res.item_id) {
        setIsInLibrary(true);
        // Update the item id so editing/saving works
        setData((prev) => prev ? {
          ...prev,
          item: { ...prev.item, id: res.item_id! }
        } : prev);
        // Optionally navigate to the new item
        navigate(`/transcript/${res.item_id}`, { replace: true });
      } else if (res.job_id) {
        // Job started - redirect to library to see it processing
        navigate("/library");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add to library");
    } finally {
      setAddingToLibrary(false);
    }
  }

  // --- Markdown export / copy ---

  function buildMarkdown(): string {
    if (!data) return "";
    const segs = editedSegments.length > 0 ? editedSegments : data.segments;
    const names = Array.from(new Set(segs.map((s) => s.speaker)));
    const wc = segs.reduce((acc, s) => acc + s.text.split(" ").length, 0);

    let md = `# ${data.item.title}\n\n`;
    if (data.item.channel_name) md += `**Channel:** ${data.item.channel_name}\n`;
    md += `**Duration:** ${data.durationMs ? msToDuration(data.durationMs) : "N/A"}\n`;
    md += `**Words:** ${wc}\n`;
    md += `**Speakers:** ${names.length}\n\n`;

    md += `## Speakers\n\n`;
    names.forEach((name) => {
      md += `- ${name}\n`;
    });
    md += `\n## Transcript\n\n`;
    segs.forEach((seg) => {
      md += `**${seg.speaker}** _(${msToTimestamp(seg.start_ms || 0)})_\n${seg.text}\n\n`;
    });
    return md.trim();
  }

  function handleExportMarkdown() {
    const md = buildMarkdown();
    if (!md || !data) return;
    const blob = new Blob([md], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${data.item.title}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  async function handleCopyForChatGPT() {
    const md = buildMarkdown();
    if (!md) return;
    await navigator.clipboard.writeText(md);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function handleExportPdf() {
    if (!data) return;
    const segs = editedSegments.length > 0 ? editedSegments : data.segments;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - margin * 2;
    let yPos = 20;

    // Title
    doc.setFontSize(18);
    doc.setFont("helvetica", "bold");
    const titleLines = doc.splitTextToSize(data.item.title, maxWidth);
    doc.text(titleLines, margin, yPos);
    yPos += titleLines.length * 8 + 10;

    // Metadata
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    if (data.item.channel_name) {
      doc.text(`Channel: ${data.item.channel_name}`, margin, yPos);
      yPos += 5;
    }
    if (data.durationMs) {
      doc.text(`Duration: ${msToDuration(data.durationMs)}`, margin, yPos);
      yPos += 5;
    }
    const wc = segs.reduce((acc, s) => acc + s.text.split(" ").length, 0);
    doc.text(`Words: ${wc}`, margin, yPos);
    yPos += 5;
    const sc = new Set(segs.map((s) => s.speaker)).size;
    doc.text(`Speakers: ${sc}`, margin, yPos);
    yPos += 10;

    // Summary section
    if (data.item.summary) {
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Summary", margin, yPos);
      yPos += 7;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      const summaryLines = doc.splitTextToSize(data.item.summary, maxWidth);
      for (const line of summaryLines) {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, margin, yPos);
        yPos += 5;
      }
      yPos += 10;
    }

    // Transcript section
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Transcript", margin, yPos);
    yPos += 10;

    doc.setFontSize(10);
    for (const seg of segs) {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }

      // Speaker name and timestamp
      doc.setFont("helvetica", "bold");
      const ts = msToTimestamp(seg.start_ms || 0);
      doc.text(`${seg.speaker} (${ts})`, margin, yPos);
      yPos += 5;

      // Text
      doc.setFont("helvetica", "normal");
      const textLines = doc.splitTextToSize(seg.text, maxWidth);
      for (const line of textLines) {
        if (yPos > 270) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, margin, yPos);
        yPos += 5;
      }
      yPos += 5;
    }

    const filename = `${data.item.title.replace(/[^a-z0-9]/gi, "_").substring(0, 50)}.pdf`;
    doc.save(filename);
  }

  // --- Guards ---

  if (authLoading) return null;
  // Guests can view their own transcripts (apiFetch sends X-Guest-Session)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <p className="mb-4 text-sm text-red-600">{error || "Transcript not found"}</p>
        <button
          onClick={() => navigate(fromFolderId ? `/library?folder=${fromFolderId}` : '/library')}
          className="text-sm font-medium text-blue-600 hover:text-blue-500"
        >
          Back to Library
        </button>
      </div>
    );
  }

  // Use editedSegments for all display
  const segments = editedSegments.length > 0 ? editedSegments : data.segments;
  const wordCount = segments.reduce((acc, seg) => acc + seg.text.split(" ").length, 0);
  const speakerCount = new Set(segments.map((s) => s.speaker)).size;

  // The Standard/Edit toggle normally lives in the audio bar; this is true when that bar
  // renders. When false (e.g. private uploads with no video_id), the toolbar hosts the toggle.
  const audioBarRenders = (audioAvailable || audioLoading || audioError) && !!data.item.video_id;

  function handleExport(format: ExportFormat) {
    if (!data) return;
    buildExport(format, segments, data.item.title, data.durationMs);
  }

  // Speaker metadata from speakers array (enriched with bios/images) or fallback to speakers_json
  let speakerMetas: SpeakerMeta[] = [];
  if (data.item.speakers && Array.isArray(data.item.speakers)) {
    // Prefer enriched speakers array from API
    speakerMetas = data.item.speakers.filter(Boolean);
  } else if (data.item.speakers_json) {
    try {
      speakerMetas = (JSON.parse(data.item.speakers_json) as SpeakerMeta[]).filter(Boolean);
    } catch {
      /* ignore */
    }
  }
  const uniqueNames = Array.from(new Set(segments.map((s) => s.speaker || "Unknown")));

  // Avatar colours by order of appearance (mirrors transcript-display.tsx) so
  // speakers with similar names don't collide on the same hash-derived colour.
  const speakerColorMap = new Map<string, string>();
  {
    let __colorIdx = 0;
    for (const s of segments) {
      const key = s.speaker || "Unknown";
      if (!speakerColorMap.has(key)) {
        speakerColorMap.set(key, AVATAR_COLORS[__colorIdx % AVATAR_COLORS.length]);
        __colorIdx++;
      }
    }
  }

  async function handleTranscribeRemaining() {
    if (!data?.item.id) return;
    setUnlockingRemaining(true);
    setUnlockError("");
    setUnlockMessage("");
    try {
      const res = await apiFetch<{ item: TranscriptItem; minutes_remaining?: number }>(
        `/library/items/${data.item.id}/transcribe-remaining`,
        { method: "POST" },
      );
      const item = res.item;
      const segments: Segment[] = item.transcript_json
        ? JSON.parse(item.transcript_json)
        : [];
      const lastSeg = segments[segments.length - 1];
      const durationMs = item.duration_ms || lastSeg?.end_ms || 0;
      setData({ item, segments, durationMs });
      setEditedSegments(segments.map((s) => ({ ...s })));
      setDirty(false);
      setUnlockMessage("Full transcript unlocked");
      window.dispatchEvent(new CustomEvent("echonour:balance-refresh"));
    } catch (err) {
      if (err instanceof InsufficientBalanceError) {
        setBalanceModal({
          minutesNeeded: err.minutes_needed,
          minutesRemaining: err.minutes_remaining,
        });
      } else {
        setUnlockError(err instanceof Error ? err.message : "Failed to unlock full transcript");
      }
    } finally {
      setUnlockingRemaining(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-2 sm:px-4 py-4 sm:py-6">
      {balanceModal && (
        <InsufficientBalanceModal
          minutesNeeded={balanceModal.minutesNeeded}
          minutesRemaining={balanceModal.minutesRemaining}
          onClose={() => setBalanceModal(null)}
        />
      )}
      {/* Save feedback */}
      {saveStatus === "success" && !dirty && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
          Changes saved successfully
        </div>
      )}
      {saveStatus === "error" && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
          Failed to save changes. Please try again.
        </div>
      )}
      {unlockMessage && (
        <div className="mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700">
          {unlockMessage}
        </div>
      )}
      {unlockError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-600">
          {unlockError}
        </div>
      )}

      {data.item.was_truncated && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <p className="font-medium">Partial transcript</p>
          <p className="mt-1 text-xs text-amber-700">
            This transcript was saved after reaching the available transcription limit, so only the transcribed portion is shown.
          </p>
        </div>
      )}

      {/* Add to Library banner (for cached transcripts not in user's library) */}
      {!isInLibrary && user && (
        <div className="mb-4 flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <div>
            <p className="text-sm font-medium text-blue-900">This transcript is from the shared cache</p>
            <p className="text-xs text-blue-700">Add it to your library to use it in Chat and save notes</p>
          </div>
          <button
            onClick={handleAddToLibrary}
            disabled={addingToLibrary}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {addingToLibrary ? "Adding..." : "Add to My Library"}
          </button>
        </div>
      )}

      {/* Back link - Issue 4: Preserve folder context */}
      <button
        onClick={() => navigate(fromFolderId ? `/library?folder=${fromFolderId}` : '/library')}
        className="mb-2 text-sm text-gray-500 hover:text-gray-700"
      >
        &larr; Library
      </button>

      {/* Header */}
      <h1 className="mb-2 flex flex-wrap items-baseline gap-2 text-lg font-semibold text-gray-900">
        {data.item.title}
      </h1>

      {/* Subtitle */}
      <div className="mb-6 flex flex-wrap items-center gap-1.5 text-sm text-gray-500">
        {data.item.channel_name && (
          <>
            {data.item.feed_url ? (
              <button
                onClick={() => navigate(`/browse/podcast?feed=${encodeURIComponent(data.item.feed_url!)}`)}
                className="font-medium text-blue-600 hover:text-blue-500 hover:underline"
              >
                {data.item.channel_name}
              </button>
            ) : (
              <span className="font-medium text-gray-700">{data.item.channel_name}</span>
            )}
            <span className="text-gray-300">&middot;</span>
          </>
        )}
        {(data.item.youtube_url || data.item.url) && (
          <>
            <a
              href={data.item.youtube_url || data.item.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-500 hover:underline"
            >
              {data.item.youtube_url || data.item.url?.includes('youtube') ? 'YouTube' : 'Source'}
            </a>
            <span className="text-gray-300">&middot;</span>
          </>
        )}
        <span>
          {new Date(data.item.published_date || data.item.created_at).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
          })}
        </span>
      </div>

      {/* Metadata */}
      <MetadataPanel
        duration={data.durationMs ? msToDuration(data.durationMs) : "N/A"}
        wordCount={wordCount}
        speakerCount={speakerCount}
      />

      {/* Summary */}
      {data.item.summary && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Summary
          </h2>
          <p className="text-sm leading-relaxed text-gray-700">
            {data.item.summary}
          </p>
        </div>
      )}

      {/* Speakers */}
      {speakerCount > 0 && (
        <div className="mb-6">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Speakers
          </h2>
          <div className="flex flex-wrap gap-3">
            {uniqueNames.map((name) => {
              const info = speakerMetas.find(
                (sp) => sp.display_name === name || sp.name === name || sp.id === name,
              );
              return (
                <div
                  key={name}
                  className="flex items-start gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2"
                >
                  <SpeakerAvatar
                    name={name}
                    imageUrl={info?.image_url}
                    size="md"
                    className={speakerColorMap.get(name)}
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      {editingBadgeName === name ? (
                        <input
                          type="text"
                          autoFocus
                          value={editingBadgeValue}
                          onChange={(e) => setEditingBadgeValue(e.target.value)}
                          onBlur={commitBadgeEdit}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") commitBadgeEdit();
                            if (e.key === "Escape") cancelBadgeEdit();
                          }}
                          className="w-32 rounded border border-blue-300 px-1 py-0.5 text-sm font-medium text-gray-900 outline-none focus:ring-1 focus:ring-blue-400"
                        />
                      ) : (
                        <span
                          className="cursor-pointer text-sm font-medium text-gray-900 hover:text-blue-600 hover:underline"
                          onClick={() => startBadgeEdit(name)}
                          title="Click to edit speaker name"
                        >
                          {name}
                        </span>
                      )}
                    </div>
                    {info?.role && info.role !== "unknown" && (
                      <p className="text-xs text-gray-500">{info.role}</p>
                    )}
                    {info?.bio && (
                      <p className="text-xs text-gray-400">{info.bio}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {(["txt", "srt", "vtt", "json"] as ExportFormat[]).map((fmt) => (
          <button
            key={fmt}
            onClick={() => handleExport(fmt)}
            className="rounded-md border px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
          >
            Export {fmt.toUpperCase()}
          </button>
        ))}

        <button
          onClick={handleExportPdf}
          className="rounded-md border px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
          PDF
        </button>
        <button
          onClick={handleExportMarkdown}
          className="rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
        >
          AI Ready
        </button>
        <button
          onClick={handleCopyForChatGPT}
          className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
            copied
              ? "border-green-300 bg-green-50 text-green-700"
              : "border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
          }`}
        >
          {copied ? "Copied!" : "Copy"}
        </button>

        {/* Chat about this transcript */}
        <button
          onClick={() => navigate(`/chat?item=${data.item.id}`)}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
        >
          💬 Chat
        </button>
        {data.item.was_truncated && user && (
          <button
            onClick={handleTranscribeRemaining}
            disabled={unlockingRemaining}
            className="rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            {unlockingRemaining ? "Unlocking..." : "Transcribe the rest"}
          </button>
        )}
      </div>

      {/* Audio Player — RSS sources only, Otter-style custom player (hidden when synced view is active) */}
      {(audioAvailable || audioLoading || audioError) && data.item.video_id && (
        <div className="sticky top-14 z-30 w-full max-w-none sm:max-w-4xl lg:max-w-6xl mx-auto px-1 sm:px-4 mb-3">
          <div className="flex items-center gap-3 rounded-lg border-2 border-[#2563eb] bg-white px-4 py-3 shadow-sm">
            {audioLoading ? (
              <div className="flex h-[46px] flex-1 items-center gap-2 rounded-full bg-gray-100 px-4">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
                <span className="text-sm text-gray-500">Loading audio...</span>
              </div>
            ) : audioError ? (
              <div className="flex h-[46px] flex-1 items-center gap-2 rounded-full bg-red-50 px-4">
                <span className="text-sm text-red-500">Failed to load audio</span>
                <button
                  onClick={() => fetchAudioInfo(data.item.video_id!)}
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  Retry
                </button>
              </div>
            ) : audioSrc ? (
              <>
                {/* Hidden native audio element — provides all browser playback APIs */}
                <audio
                  ref={audioRef}
                  src={audioSrc}
                  preload="metadata"
                  className="sr-only"
                  onTimeUpdate={(e) => setAudioCurrentTimeMs(Math.floor(e.currentTarget.currentTime * 1000))}
                  onPlay={() => setAudioIsPlaying(true)}
                  onPause={() => setAudioIsPlaying(false)}
                  onEnded={() => setAudioIsPlaying(false)}
                  onLoadedMetadata={(e) => setAudioDurationMs(Math.floor(e.currentTarget.duration * 1000))}
                >
                  Your browser does not support the audio element.
                </audio>

                {/* 46px round play/pause button */}
                <button
                  onClick={() => {
                    const audio = audioRef.current;
                    if (!audio) return;
                    if (audioIsPlaying) audio.pause();
                    else audio.play().catch(() => {});
                  }}
                  className="flex h-[46px] w-[46px] shrink-0 items-center justify-center rounded-full bg-[#2563eb] text-white shadow-sm transition-colors hover:bg-[#1d4ed8] active:bg-[#1d4ed8]"
                  aria-label={audioIsPlaying ? 'Pause' : 'Play'}
                >
                  {audioIsPlaying ? (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                      <path fillRule="evenodd" d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25Zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25Z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5 translate-x-0.5">
                      <path fillRule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>

                {/* JetBrains Mono time display, min-width 104px */}
                <span
                  className="shrink-0 tabular-nums text-sm text-[#1f2430]"
                  style={{ fontFamily: 'JetBrains Mono, monospace', minWidth: '104px' }}
                >
                  {msToDuration(audioCurrentTimeMs)} / {audioDurationMs > 0 ? msToDuration(audioDurationMs) : '--:--'}
                </span>

                {/* Thin scrub bar with 14px knob */}
                <div
                  className="relative flex-1 cursor-pointer py-3"
                  onClick={(e) => {
                    const audio = audioRef.current;
                    if (!audio || audioDurationMs === 0) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
                    const seekMs = fraction * audioDurationMs;
                    audio.currentTime = seekMs / 1000;
                    setAudioCurrentTimeMs(seekMs);
                  }}
                >
                  <div className="relative h-1 rounded-full bg-[#e7e9ee]">
                    <div
                      className="absolute left-0 top-0 h-full rounded-full bg-[#2563eb] pointer-events-none transition-none"
                      style={{ width: `${audioDurationMs > 0 ? (audioCurrentTimeMs / audioDurationMs) * 100 : 0}%` }}
                    />
                    <div
                      className="absolute top-1/2 -translate-y-1/2 h-[14px] w-[14px] rounded-full border-2 border-white bg-[#2563eb] shadow-sm pointer-events-none"
                      style={{ left: `calc(${audioDurationMs > 0 ? (audioCurrentTimeMs / audioDurationMs) * 100 : 0}% - 7px)` }}
                    />
                  </div>
                </div>
              </>
            ) : null}

            {/* In-player Standard/Edit toggle — always visible, right side of the bar */}
            <div className="ml-auto flex items-center">
              <div className="flex overflow-hidden rounded-md border border-gray-200">
                <button
                  type="button"
                  onClick={() => setViewMode('standard')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                    viewMode === 'standard'
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Standard
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('edit')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
                    viewMode === 'edit'
                      ? 'bg-[#2563eb] text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9"/>
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
                  </svg>
                  Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transcript */}
      <TranscriptDisplay
          transcript={segments}
          editable={viewMode === 'edit'}
          onEditText={handleEditText}
          onEditSpeaker={handleEditSpeaker}
          onEditSpeakerSingle={handleEditSpeakerSingle}
          onSplit={handleSplit}
          onEditSegmentTs={handleEditSegmentTs}
          onEditWordTs={handleEditWordTs}
          durationMs={data.durationMs}
          speakerMetas={speakerMetas}
          currentTimeMs={audioCurrentTimeMs}
          hasAudioBarAbove={audioAvailable && !!data.item.video_id}
          onSeekAudio={audioAvailable ? (ms: number) => {
            const audio = audioRef.current;
            if (!audio) return;
            const seekTo = ms / 1000;
            if (audio.readyState >= 1) {
              // Metadata loaded — seek immediately
              audio.currentTime = seekTo;
              audio.play().catch(() => {});
            } else {
              // Audio not ready yet — queue the seek for when metadata arrives
              const onReady = () => {
                audio.currentTime = seekTo;
                audio.play().catch(() => {});
                audio.removeEventListener('loadedmetadata', onReady);
              };
              audio.addEventListener('loadedmetadata', onReady);
            }
            setAudioCurrentTimeMs(ms);
          } : undefined}
          initialScrollToIndex={initialScrollToIndex}
          onInitialScrollComplete={handleInitialScrollComplete}
          onSegmentSpeakerOverride={speakerOverrideEnabled && !!user ? handleSegmentSpeakerOverride : undefined}
          onMergeWithPrev={viewMode === 'edit' ? handleMergeWithPrev : undefined}
          onMergeWithNext={viewMode === 'edit' ? handleMergeWithNext : undefined}
          onSaveAndExit={handleSave}
      />

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <div className="mt-10 rounded-lg bg-gray-50 p-6">
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Recommended
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3">
            {recommendations.map((rec) => (
              <button
                key={rec.video_id}
                onClick={() => navigate(`/?url=${encodeURIComponent(rec.url)}`)}
                className="flex flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left transition-shadow hover:shadow-md"
              >
                <Thumbnail
                  src={rec.thumbnail_url}
                  alt={rec.title ?? ""}
                  className="aspect-video w-full object-cover"
                />
                <div className="flex flex-1 flex-col gap-1 p-2 sm:p-3">
                  <span className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2">
                    {rec.title}
                  </span>
                  {rec.channel_name && (
                    <span className="text-[10px] sm:text-xs text-gray-500">{rec.channel_name}</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Merge undo toast — appears after a merge, dismisses after 15s */}
      {lastMergeUndo && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-lg bg-gray-900 px-4 py-3 text-white shadow-lg">
          <span className="text-sm">Segments merged</span>
          <button
            onClick={handleUndoMerge}
            className="text-sm font-semibold text-blue-300 hover:text-blue-200"
          >
            Undo
          </button>
        </div>
      )}

      {/* Fixed Standard/Edit toggle — always pinned top-right for items without an audio bar
          (which hosts its own in-player toggle). Always reachable, scrolled or not. */}
      {!audioBarRenders && (
        <div className="fixed right-4 top-20 z-40 flex overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg">
          <button
            type="button"
            onClick={() => setViewMode('standard')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === 'standard'
                ? 'bg-[#2563eb] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            Standard
          </button>
          <button
            type="button"
            onClick={() => setViewMode('edit')}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === 'edit'
                ? 'bg-[#2563eb] text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9"/>
              <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/>
            </svg>
            Edit
          </button>
        </div>
      )}
    </div>
  );
}
